/**
 * Enhanced AdminUserViewPage using the new component system
 * Demonstrates migration from old patterns to atomic design components
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEdit,
  FiKey,
  FiTrash2,
  FiUsers,
  FiUserCheck,
  FiSettings,
  FiSearch,
  FiUpload,
} from 'react-icons/fi';
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';

// New component system imports
import { CrudPageLayout, ConfirmModal, TableColumn } from '@grcs/components';

// Existing hooks and types
import {
  useStudentOperations,
  useUserOperations,
  useSemesterOperations,
} from '@grcs/hook/useDatabase';
import { StudentProfile, Semester } from '@grcs/types/DataTypes';
import { UserProfile, UserRole } from '@grcs/types/UserRole';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import { useAccountManagement } from '@grcs/hook/useAccountManagement';
import { useData } from '@grcs/context/DataContext';

type DeleteId = {
  user_token: string;
  auth_uid: string;
};

type EnhancedUserProfile = UserProfile & {
  class_name?: string;
  semester_year?: string;
  status?: 'active' | 'deleted';
};

interface AdminUserViewPageProps {
  user_role: UserRole;
}

export const AdminUserViewPageEnhanced: React.FC<AdminUserViewPageProps> = ({
  user_role,
}) => {
  const { supabase } = useData();
  const userOps = useUserOperations();
  const studentOps = useStudentOperations();
  const semesterOps = useSemesterOperations();
  const navigate = useNavigate();
  const accounts = useAccountManagement();
  const toast = useToast();
  const [resetTarget, setResetTarget] = useState<EnhancedUserProfile | null>(
    null,
  );
  const [isResetting, setIsResetting] = useState(false);
  const resetPending = useRef(false);

  const handleConfirmReset = async () => {
    if (!resetTarget?.user_token || resetPending.current) return;
    resetPending.current = true;
    setIsResetting(true);
    try {
      await accounts.resetDefaultPassword(resetTarget.user_token);
      setResetTarget(null);
      toast({
        title: 'Password reset to birthday',
        description:
          'The password is now the saved birthday in YYYYMMDD format.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Password reset failed',
        description:
          error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      resetPending.current = false;
      setIsResetting(false);
    }
  };
  const muted = useColorModeValue('gray.600', 'gray.300');

  const [users, setUsers] = useState<EnhancedUserProfile[]>([]);
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [semesters, setSemesters] = useState<Semester[]>([]);
  const [search, setSearch] = useState('');
  const [selectedSemester, setSelectedSemester] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [deleteIds, setDeleteIds] = useState<DeleteId>({} as DeleteId);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Determine display role
  const displayRole = () => {
    switch (user_role) {
      case UserRole.STUDENT:
        return 'Student';
      case UserRole.TEACHER:
        return 'Teacher';
      case UserRole.ADMIN:
        return 'Admin';
      default:
        return 'User';
    }
  };

  // Get appropriate icon for user role
  const getRoleIcon = () => {
    switch (user_role) {
      case UserRole.STUDENT:
        return <FiUsers />;
      case UserRole.TEACHER:
        return <FiUserCheck />;
      case UserRole.ADMIN:
        return <FiSettings />;
      default:
        return <FiUsers />;
    }
  };

  // Get role-specific color scheme
  const getRoleColorScheme = () => {
    switch (user_role) {
      case UserRole.STUDENT:
        return 'blue';
      case UserRole.TEACHER:
        return 'green';
      case UserRole.ADMIN:
        return 'purple';
      default:
        return 'gray';
    }
  };

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error } = await userOps.getUsersByRole(user_role);
        if (error) {
          setError('Failed to load users');
          console.error('Error fetching users:', error);
        } else {
          const sortedUsers = (data || []).slice().sort((a, b) => {
            const firstNameA = (a.first_name || '').toLowerCase();
            const firstNameB = (b.first_name || '').toLowerCase();
            return firstNameA.localeCompare(firstNameB);
          });
          setUsers(sortedUsers as EnhancedUserProfile[]);
        }
      } catch (err) {
        setError('Failed to load users');
        console.error('Error fetching users:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [userOps, user_role]);

  // Fetch students (for student role only)
  useEffect(() => {
    if (user_role !== UserRole.STUDENT) return;

    const fetchStudents = async () => {
      try {
        const { data, error } = await studentOps.getAllStudents();
        if (error) {
          console.error('Error fetching students:', error);
        } else {
          setStudents(data || []);
        }
      } catch (err) {
        console.error('Error fetching students:', err);
      }
    };

    fetchStudents();
  }, [studentOps, user_role]);

  // Fetch semesters (for student role only)
  useEffect(() => {
    if (user_role !== UserRole.STUDENT) return;

    const fetchSemesters = async () => {
      try {
        console.log('📅 Loading semesters for student filter...');
        const { data, error } = await semesterOps.getAllSemesters();
        if (error) {
          console.error('Error fetching semesters:', error);
        } else {
          const sortedSemesters = (data || []).sort(
            (a, b) => parseInt(b.semester_year) - parseInt(a.semester_year),
          );
          setSemesters(sortedSemesters);
          console.log(`✅ Loaded ${sortedSemesters.length} semesters`);
        }
      } catch (err) {
        console.error('Error fetching semesters:', err);
      }
    };

    fetchSemesters();
  }, [semesterOps, user_role]);

  // Create students lookup map
  const studentsByToken = students.reduce<Record<string, StudentProfile>>(
    (acc, student) => {
      acc[student.student_token] = student;
      return acc;
    },
    {},
  );

  // Enhance users with student data and apply semester filter
  const enhancedUsers = users
    .map((user) => {
      const student = user.user_token
        ? studentsByToken[user.user_token]
        : undefined;

      return {
        ...user,
        class_name: student?.class_name,
        semester_year: student?.semester_year,
        status: (student?.deleted ? 'deleted' : 'active') as
          | 'active'
          | 'deleted',
      };
    })
    .filter((user) => {
      // Apply semester filter for students only
      if (
        user_role === UserRole.STUDENT &&
        selectedSemester &&
        selectedSemester.trim()
      ) {
        return user.semester_year === selectedSemester;
      }
      return true;
    });

  // Pagination calculations
  const totalPages = Math.ceil(enhancedUsers.length / pageSize);
  const paginationConfig = {
    currentPage,
    totalPages,
    pageSize,
    totalItems: enhancedUsers.length,
    showPageSizeSelector: true,
    onPageChange: (page: number) => setCurrentPage(page),
    onPageSizeChange: (newPageSize: number) => {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when changing page size
    },
  };

  // Handle search change
  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // Handle search
  const handleSearch = async (searchTerm: string) => {
    setSearch(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
    setIsLoading(true);
    setError(null);

    try {
      if (!searchTerm.trim()) {
        const { data, error: searchError } = await userOps.getUsersByRole(
          user_role,
        );
        if (searchError) throw searchError;
        setUsers(data || []);
      } else {
        const { data, error: searchError } = await supabase
          .from(TABLE_NAMES.USER)
          .select()
          .eq('role', user_role)
          .eq('deleted', false)
          .textSearch('full_name', searchTerm, { type: 'websearch' });
        if (searchError) throw searchError;

        setUsers(data || []);
      }
    } catch (err) {
      setError('Search failed');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle semester filter change
  const handleSemesterFilter = (semester: string) => {
    console.log('🔄 Semester filter changed to:', semester);
    setSelectedSemester(semester);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle delete
  const handleDeleteClick = (user: EnhancedUserProfile) => {
    if (user.user_token) {
      setDeleteIds({
        user_token: user.user_token,
        auth_uid: user.auth_uid || '',
      });
      setIsDeleteOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    try {
      await userOps.removeUserCompletely(
        deleteIds.user_token,
        deleteIds.auth_uid,
      );

      setUsers((prev) =>
        prev.filter((u) => u.user_token !== deleteIds.user_token),
      );

      setIsDeleteOpen(false);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle navigation
  const handleEdit = (user: EnhancedUserProfile) => {
    navigate(`/admin/user/${user.user_token}`);
  };

  const handleAddNewUser = () => {
    navigate(`/admin/user/new/${user_role}`);
  };

  const handleBulkUpload = () => {
    navigate('/admin/student/bulk');
  };

  // Define table columns
  const columns: TableColumn<EnhancedUserProfile>[] = [
    {
      key: 'first_name',
      header: 'First Name',
      sortable: true,
    },
    {
      key: 'last_name',
      header: 'Last Name',
      sortable: true,
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
    },
    ...(user_role === UserRole.STUDENT
      ? ([
          {
            key: 'username',
            header: 'Username',
          },
          {
            key: 'class_name',
            header: 'Class',
            render: (user: EnhancedUserProfile, value: string) => (
              <div>
                {value}
                {user.status === 'deleted' && (
                  <Badge colorScheme="red" ml={2} size="sm">
                    Deleted
                  </Badge>
                )}
              </div>
            ),
          },
          {
            key: 'semester_year',
            header: 'Semester',
          },
        ] as TableColumn<EnhancedUserProfile>[])
      : []),
    {
      key: 'dob',
      header: 'Date of Birth',
    },
  ];

  return (
    <>
      <CrudPageLayout
        compact
        title={`${displayRole()}s`}
        subtitle={
          user_role === UserRole.STUDENT
            ? 'Manage student accounts, enrollment, and semester records.'
            : 'Manage teacher accounts and contact information.'
        }
        headerIcon={getRoleIcon()}
        headerBadge={{
          text: `${isLoading ? '—' : enhancedUsers.length} ${
            enhancedUsers.length === 1
              ? displayRole().toLowerCase()
              : displayRole().toLowerCase() + 's'
          }${
            user_role === UserRole.STUDENT &&
            selectedSemester &&
            selectedSemester.trim()
              ? ` (${selectedSemester})`
              : ''
          }`,
          colorScheme: getRoleColorScheme(),
          variant: 'subtle',
        }}
        headerVariant="elevated"
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: displayRole() + 's', isCurrent: true },
          ],
        }}
        createButton={{
          label: `Add New ${displayRole()}`,
          onClick: handleAddNewUser,
        }}
        headerActions={
          user_role === UserRole.STUDENT ? (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiUpload />}
              onClick={handleBulkUpload}
            >
              Upload students
            </Button>
          ) : undefined
        }
        table={{
          data: enhancedUsers,
          columns,
          loading: isLoading,
          error: error ? new Error(error) : undefined,
          clickableRows: true,
          onRowClick: handleEdit,
          pagination: paginationConfig,
          actions: [
            {
              label: 'Edit',
              icon: <FiEdit />,
              onClick: handleEdit,
              variant: 'secondary',
            },
            {
              label: 'Reset password',
              icon: <FiKey />,
              onClick: (user: EnhancedUserProfile) => {
                if (!resetPending.current) setResetTarget(user);
              },
              variant: 'secondary',
            },
            {
              label: 'Delete',
              icon: <FiTrash2 />,
              onClick: handleDeleteClick,
              variant: 'danger',
            },
          ],
          emptyState: (
            <VStack spacing={2} py={8} color={muted} textAlign="center">
              <FiUsers size={28} />
              <Text fontWeight="600">
                No {displayRole().toLowerCase()}s found
              </Text>
              <Text fontSize="sm">
                {search || selectedSemester
                  ? 'Try another name or semester, or clear your filters.'
                  : `Add a ${displayRole().toLowerCase()} to get started.`}
              </Text>
            </VStack>
          ),
        }}
      >
        <Box
          as="form"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            handleSearch(search);
          }}
        >
          <Stack direction={{ base: 'column', md: 'row' }} spacing={2}>
            <InputGroup size="sm" flex={1}>
              <InputLeftElement pointerEvents="none">
                <FiSearch />
              </InputLeftElement>
              <Input
                aria-label={`Search ${displayRole().toLowerCase()}s by name`}
                placeholder="Search by name"
                value={search}
                onChange={handleSearchChange}
                borderRadius="md"
              />
            </InputGroup>
            {user_role === UserRole.STUDENT && (
              <Select
                size="sm"
                aria-label="Filter by semester"
                value={selectedSemester}
                onChange={(event) => handleSemesterFilter(event.target.value)}
                width={{ base: 'full', md: '200px' }}
                borderRadius="md"
              >
                <option value="">All semesters</option>
                {semesters.map((semester) => (
                  <option
                    key={semester.semester_year}
                    value={semester.semester_year}
                  >
                    {semester.semester_year}
                    {semester.is_current ? ' (Current)' : ''}
                  </option>
                ))}
              </Select>
            )}
            <HStack>
              <Button
                type="submit"
                size="sm"
                colorScheme="blue"
                isLoading={isLoading}
              >
                Search
              </Button>
              {(search || selectedSemester) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    handleSemesterFilter('');
                    handleSearch('');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </HStack>
          </Stack>
          <Text fontSize="xs" color={muted} mt={2} role="status">
            {isLoading
              ? 'Loading accounts…'
              : `${enhancedUsers.length} ${displayRole().toLowerCase()}${
                  enhancedUsers.length === 1 ? '' : 's'
                } found`}
          </Text>
        </Box>
      </CrudPageLayout>

      <ConfirmModal
        isOpen={!!resetTarget}
        onClose={() => {
          if (!resetPending.current) setResetTarget(null);
        }}
        type="warning"
        title="Reset password to birthday"
        message={`Reset the password for ${resetTarget?.first_name || ''} ${
          resetTarget?.last_name || ''
        } (${resetTarget?.email || ''})?`}
        description="This replaces the current password with the saved birthday in YYYYMMDD format and takes effect immediately."
        onConfirm={handleConfirmReset}
        confirmText="Reset password"
        loading={isResetting}
      />
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        type="danger"
        title={`Delete ${displayRole()}`}
        message={`Are you sure you want to delete this ${displayRole().toLowerCase()}?`}
        description="This action cannot be undone and will permanently remove all associated data."
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        loading={isDeleting}
      />
    </>
  );
};
